import React, { Fragment } from 'react';
import cloneDeep from 'lodash.clonedeep';
import cx from 'classnames';
import withStyles from '@mui/styles/withStyles';
import Hidden from './Hidden';
import { compose, withApollo } from '../reactApolloCompat';
import { withRouter } from '../utils/withRouter';
import Spinner from './loading/Spinner';
import {
    deleteTypeName,
    diff,
    getProperty,
    isNullOrUndefined,
    mergeDataSources,
    resetAllProperties,
    setProperty,
    stripUnchanged
} from '../utils/objects';
import Button from './form/Button';
import { gqlArr as gql } from '../utils/fragment';
import { removeById } from '../utils/arrays';
import { enableValidation, validationUtility } from '../utils/validation';
import Inline from './layout/Inline';
import { SaveIcon, TickIcon } from './IconIndex';
import { withSnackbarMessage } from '../context/SnackbarMessage';

/*
 * DataForm is used for saving DataObjects.
 */

export const DataFormSaveMode = {
    currentTab: 0,
    currentFragment: 1,
    all: 2
};

class DataForm extends React.Component {
    static defaultProps = {
        saveMode: DataFormSaveMode.currentFragment,
        saveNotValid: false
    };
    original = {};
    state = {
        validation: validationUtility.createState()
    };
    loaded = {};
    tabLookup = null;
    _isSavePending = false;
    _isModified = false;
    _isMounted = false;

    constructor(props) {
        super(props);
        // Set state function is passed down to children as a callback
        // so we need to bind the context of `this`
        this.setState = this.setState.bind(this);
    }

    unblock = () => null;

    componentWillMount() {
        const { history, parentDataForm } = this.props;
        if (!parentDataForm) {
            this.unblock = history.block(this.onHistoryBlock);
        }
        this.updateFormState(this.props);
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.data !== this.props.data) {
            const hasDiff = diff(nextProps.data, this.props.data, false);
            if (Object.keys(hasDiff).length) {
                this.updateFormState(nextProps);
            }
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.unblock();
    }

    getValidation = (fieldName, forceFieldNameAsKey = false, revalidate = false) => {
        const { validation } = this.state;
        return validationUtility.getValidationResult(fieldName, validation, forceFieldNameAsKey, revalidate);
    };

    render() {
        const { children, error, loading, data, readOnly, createNew, onStepperNext } = this.props;
        if (error) {
            console.error(error);
            return <div>Error loading data.</div>;
        }

        if (!loading && !data && !createNew) {
            return <div>Data source not found.</div>;
        }

        const form = this.generateForm();

        const buttons =
            readOnly === true || loading === true
                ? null
                : isNullOrUndefined(onStepperNext)
                    ? this.renderAllButtons()
                    : this.renderStepperNext();
        return <Fragment>{children(form, buttons)}</Fragment>;
    }

    renderAllButtons() {
        const { additionalActions, readOnly, buttonContainerStyles } = this.props;

        const saveButton = readOnly ? [null, true] : this.renderSave();

        return (
            <Inline nowrap footer className={buttonContainerStyles}>
                {additionalActions &&
                    additionalActions.map((action, i) => {
                        if (!action.component) action.component = Button;
                        return (
                            <action.component
                                key={'action' + i}
                                variant={action.variant}
                                onClick={() => action.onClick()}
                                disabled={(!action.skipSave && !!saveButton[1]) || !!action.disabled || this.savePending}
                                startIcon={action.startIcon || action.icon}
                                endIcon={action.endIcon}
                            >
                                {(!!action.startIcon || !!action.icon || !!action.endIcon) ?
                                    <Hidden xsDown>{action.label}</Hidden> : action.label}
                            </action.component>
                        );
                    })}
                {saveButton[0]}
            </Inline>
        );
    }

    validateForm = () => {
        const { validation } = this.props;
        let result = !isNullOrUndefined(validation)
            ? validationUtility.validate(this, validation, true)
            : validationUtility.validateTabs(this, this.tabLookup, true);
        this.generateForm();
        return result;
    };

    generateForm() {
        const { loading: queryLoading } = this.props;
        const { validation } = this.state;
        if (!this.form) {
            const whom = this;
            this.form = {
                state: this.getTabState(),
                setState: this.setNestedState,
                getState: this.getNestedState,
                addToNestedArray: this.addToNestedArray,
                removeFromNestedArray: this.removeFromNestedArray,
                loading: queryLoading,
                validation,
                getValidation: this.getValidation,
                validateForm: this.validateForm,
                invalidate: allTabs => this.invalidate(allTabs),
                save: callback => this.executeManualSave(callback),
                getOriginal: property => getProperty(this.getOriginalState(), property),
                get isDirty() {
                    return whom.verifyIsModified();
                },
                getDirty: () => whom.verifyIsModified(true),

                //legacy, for compatibility:
                validateField: () => null,
                setField: this.setNestedState,
                getField: this.getNestedState
            };
        } else {
            this.form.state = this.getTabState();
            this.form.loading = queryLoading;
            this.form.validation = validation;
        }
        return this.form;
    }

    getStateID(tabId) {
        const { saveMode, tab } = this.props;
        let stateID = false;
        switch (saveMode) {
            case DataFormSaveMode.currentFragment:
                if (tab !== undefined) {
                    if (tab.fragment.UniqueID === undefined) {
                        //ok, so we need a way to differentiate fragments, but there is no neat way to do this.
                        //lets use id of the first tab using this fragment as uniqueid
                        tab.fragment.UniqueID = tab.id;
                    }
                    stateID = tab.fragment.UniqueID;
                }
                break;
            case DataFormSaveMode.all:
                stateID = 'Combined';
                break;
            case DataFormSaveMode.currentTab:
            default:
                stateID = tabId || tab.id;
                break;
        }
        return stateID;
    }

    //gets the state relative to the current tab
    getTabState(tabId) {
        const stateId = this.getStateID(tabId);
        return this.state[stateId];
    }

    //sets the state specific to the current tab
    setTabState(state, tabId) {
        const stateId = this.getStateID(tabId);
        this.setState({ [stateId]: state });
    }

    //gets the original state relative to the current tab
    getOriginalState(tabId) {
        const stateId = this.getStateID(tabId);
        return this.original[stateId] || {};
    }

    //sets the original state specific to the current tab
    setOriginalState(state, tabId) {
        const { context } = this.props;
        const stateId = this.getStateID(tabId);
        this.original[stateId] = state;
        this.verifyIsModified();
        !!context && context.forceUpdate();
    }

    getNestedState = field => {
        return getProperty(this.getTabState(), field);
    };

    setNestedState = (newState, includeOriginal = false) => {
        const state = this.getTabState();
        for (let fieldName in newState) {
            setProperty(state, fieldName, newState[fieldName]);
        }
        this.setTabState(state);
        if (includeOriginal) {
            const original = this.getOriginalState();
            for (let fieldName in newState) {
                setProperty(original, fieldName, newState[fieldName]);
            }
        }
        this.verifyIsModified();
    };

    addToNestedArray = (item, arrayName, context) => {
        const array = this.getNestedState(arrayName);
        array.push(item);
        this.setNestedState({ [arrayName]: array }, true);
        context.forceUpdate();
    };

    removeFromNestedArray = (itemId, arrayName, context) => {
        const array = this.getNestedState(arrayName);
        removeById(array, itemId);
        this.setNestedState({ [arrayName]: array }, true);
        context.forceUpdate();
    };

    updateFormState(newProps) {
        const { id: nextId, tab, tabs, loading, data, onLoad, onChange, createNew, saveMode } = newProps;
        const { id: currentId } = this.props;
        if (!tab) {
            throw new Error('no tab/view supplied to dataform');
        }
        this.generateForm();
        const newState = {};
        const currentState = this.getTabState() || {};

        if (nextId !== currentId) {
            // Clear the state when loading a new record
            this.original = {};
            this.loaded = {};
            Object.keys(currentState).forEach(key => (newState[key] = undefined));
            Object.keys(tabs).forEach(key => (this.original[tabs[key].id] = {}));
        }

        if (!loading && !!data) {
            // Merge new data into the original record and state, then reapply unsaved changes to the state
            let loadedData = false;
            const wasState = currentId && currentId === currentState.ID ? currentState : {};
            const dirtyBits = createNew ? deleteTypeName(cloneDeep(data)) : stripUnchanged(wasState, this.original);
            const stateID = this.getStateID(tab.id);
            if (stateID === 'Combined' && !this.loaded[tab.id]) {
                this.loaded[tab.id] = true;
                loadedData = deleteTypeName(cloneDeep(data));
                if (onLoad) onLoad(loadedData);
                if (tab.onLoad) tab.onLoad(loadedData);
            } else if (!this.loaded[stateID]) {
                this.loaded[stateID] = true;
                loadedData = deleteTypeName(cloneDeep(data));
                if (onLoad) onLoad(loadedData);
                if (DataFormSaveMode.currentFragment === saveMode) {
                    tabs.forEach(({ fragment: { UniqueID: key }, onLoad }) => {
                        if (key === stateID) {
                            if (onLoad) onLoad(loadedData);
                        }
                    });
                } else if (tab.onLoad) tab.onLoad(loadedData);
            }

            if (!!loadedData) {
                const original = mergeDataSources(this.getOriginalState(), loadedData);
                if (!!createNew) {
                    //clear all preset properties from original (if we have default parameters)
                    resetAllProperties(original);
                }
                Object.assign(newState, mergeDataSources(original, dirtyBits)); // retain current state
                Object.assign(newState, mergeDataSources(newState, loadedData)); // but replace with newer bits
                this.setOriginalState(original, tab.id);
            }
        }

        if (!this.tabLookup) {
            this.tabLookup = tabs.reduce((acc, t) => {
                acc[t.id] = t;
                t.key = this.getStateID(t.id); //for compatibility with validation utility, which uses key
                return acc;
            }, {});
        }

        if (Object.keys(newState).length) {
            this.setTabState(newState, tab.id);
            if (onChange) setTimeout(() => onChange(this.form), 1); // pause for state to update
        }
    }

    /**
     * Copies over all ID fields from the src object to the tgt object.
     */
    mergeIds(src, tgt) {
        if (!src || !tgt) return;
        if (Array.isArray(src)) {
            src.forEach((_, i) => this.mergeIds(src[i], tgt[i]));
        } else if (typeof src === 'object') {
            //if (src.hasOwnProperty('ID')) tgt.ID = src.ID;
            if (src.hasOwnProperty('ID') && !tgt.hasOwnProperty('ID')) tgt.ID = src.ID;
            if (tgt.ID === 0 || tgt.ID === '0') tgt.ID = null; // remove ID = 0 so that new items can be saved
            Object.keys(src).forEach(k => this.mergeIds(src[k], tgt[k]));
        }
    }

    getMutation() {
        const input = cloneDeep(this.input);

        const { fragments, tabFragmentLookup, name, id, idType } = this.props;

        // Recurse all nested objects of the original and make sure IDs are included in the input.
        this.mergeIds(this.getOriginalState(), input);

        const { tab } = this.props;
        if (isNullOrUndefined(tab)) throw new Error('No tab specified');
        else {
            // Go through all modified keys to determine which tabs have been modified
            const modifiedTabs = {};
            const requiredFields = ['ID'];
            Object.keys(input).forEach(key => {
                if (requiredFields.includes(key)) return;
                const tabKeys = tabFragmentLookup[key] || [];
                tabKeys.forEach(k => {
                    if (this.tabLookup[k]) {
                        if (this.loaded[k]) modifiedTabs[k] = true;
                    } else {
                        console.error(
                            'cannot find tab ',
                            k,
                            ' in tab collection ',
                            this.tabLookup,
                            '. Check the id property of the tab'
                        );
                    }
                });
            });
            this.modifiedTabKeys = Object.keys(modifiedTabs);

            this.modifiedTabKeys.forEach(k => {
                if (DataFormSaveMode.currentFragment === this.props.saveMode) {
                    Object.keys(this.tabLookup).forEach(key => {
                        if (this.tabLookup[key].key === k) {
                            const { formatSaveData } = this.tabLookup[key];
                            if (formatSaveData) formatSaveData(input, this.getTabState(k), this.original);
                        }
                    });
                } else {
                    const { formatSaveData } = this.tabLookup[k];
                    if (formatSaveData) formatSaveData(input, this.getTabState(k), this.original);
                }
            });

            // use props.saveMode to know where original data is stored
            const tabID = this.getStateID();

            // let's strip out things that did not change.
            const source = cloneDeep(this.original);
            this.mergeIds(this.original, source);
            const changes = stripUnchanged(input, source[tabID]);
            changes.ID = input.ID;

            // Build up all the list of required fragment parts and field names to query in the response
            const fieldNames = [];
            const fragmentParts = [];
            const related = [];
            Object.keys(changes).forEach(key => {
                if (requiredFields.includes(key)) return;
                if (typeof input[key] === 'object' && input[key] !== null && input[key] !== undefined) {
                    // For object fields we find the matching fragment that selects that object
                    let fragment = fragments[key];

                    if (!fragment) {
                        //this is likely a nested fragment that needs to be resolved via the lookup
                        const tabNames = tabFragmentLookup[key];
                        if (isNullOrUndefined(tabNames)) {
                            console.log('undefined tabNames', key, tabFragmentLookup);
                            throw new Error('data form cannot resolve tab name from property in fragment lookup');
                        }

                        let identifiedFragment = null;
                        let nestedPropertyPaths = null;

                        if (tabNames.length === 1 && this.tabLookup[tabNames[0]]) {
                            identifiedFragment = this.tabLookup[tabNames[0]].fragment;
                        } else if (Array.isArray(input[key])) {
                            for (let x = 0; x < tabNames.length && identifiedFragment === null; x++) {
                                const tabName = tabNames[x];
                                if (fragments[tabName]) identifiedFragment = fragments[tabName];
                                else if (this.tabLookup[tabName]) identifiedFragment = this.tabLookup[tabName].fragment;
                            }
                        } else {
                            //this input contains modified nested properties. find the appropriate fragment
                            nestedPropertyPaths = Object.keys(input[key]).map(x => `${key}_${x}`);

                            for (let x = 0; x < tabNames.length && identifiedFragment === null; x++) {
                                const tabName = tabNames[x];
                                for (let y = 0; y < nestedPropertyPaths.length && identifiedFragment === null; y++) {
                                    const propertyPath = nestedPropertyPaths[y];
                                    const mappedTabName = tabFragmentLookup[propertyPath];
                                    if (mappedTabName) {
                                        identifiedFragment = fragments[tabName];
                                    }
                                }
                            }

                            //this is a nested object. But it's possibly a fragment. so just grab the first
                            if (isNullOrUndefined(identifiedFragment) && typeof input[key] === 'object') {
                                identifiedFragment = this.tabLookup[tabNames[0]].fragment;
                            }
                        }

                        //the data form failed to identify the tab, and subsequently the fragment that this nested property exists on
                        if (isNullOrUndefined(identifiedFragment)) {
                            console.log(
                                `data form '${name}' cannot resolve tab from property '${key}' in fragment lookup`
                            );
                            console.log('tabNames', tabNames);
                            console.log('this.tabLookup', this.tabLookup);
                            console.log('this.tabLookup[tabNames]', this.tabLookup[tabNames]);
                            console.log('tabFragmentLookup', tabFragmentLookup);
                            console.log('input', input);
                            console.log('fragments', fragments);
                            console.log('fragmentParts', fragmentParts);
                            console.log('nestedPropertyPaths', nestedPropertyPaths);
                            console.log('this.tabLookup[tabNames[0]]', this.tabLookup[tabNames[0]]);

                            throw new Error('data form cannot resolve tab from property in fragment lookup');
                        }

                        fragment = identifiedFragment;
                    }

                    fragmentParts.push(fragment);
                } else if (key.length > 2 && key.endsWith('ID')) {
                    //this is related. like ContactID. so we nest
                    const relatedFragment = key.substring(0, key.length - 2);
                    if (fragments[relatedFragment]) {
                        related.push(fragments[relatedFragment]);
                    } else {
                        const tabNames = tabFragmentLookup[relatedFragment];
                        const tabLookup = !!tabNames && this.tabLookup[tabNames[0]];
                        if (!!tabLookup) related.push(tabLookup.fragment);
                    }
                    //note: added this if condition to block from undefined nested fragments
                    //typically this works if your nested bits are defined separately, but sometimes
                    //they're defined inside the single fragment, which means they dont need to be spread
                } else {
                    fieldNames.push(key);
                }
            });

            /*
            console.log('----------------');
            console.log('fragments', fragments);
            console.log('fieldNames', fieldNames);
            console.log('fragmentParts', fragmentParts);
            console.log('related', related);
            console.log('input', {...input});
            */

            let { mutationName, mutationInputType, createNew } = this.props;

            if (createNew && !input.ID) {
                mutationName = mutationName.replace('update', 'create');
                mutationInputType = mutationInputType.replace('Update', 'Create');
                if (idType && id) {
                    input[idType] = id;
                }
            }

            return {
                mutation: gql`
                    mutation ${mutationName}($input: ${mutationInputType}!) {
                        ${mutationName}(input: $input) {
                            ${requiredFields.join('\n')}
                            ${fieldNames.join('\n')}
                            ${fragmentParts.map(f => '...' + f.definitions[0].name.value).join('\n')}
                            ${related.map(f => '...' + f.definitions[0].name.value).join('\n')}
                        }
                    }
                    ${fragmentParts}
                    ${related}
                `,

                variables: {
                    input: changes
                }
            };
        }
    }

    onHistoryBlock = location => {
        const { history, historyBlockHandler } = this.props;
        const currentParts = history.location.pathname.split('/').filter(x => x.length > 0);
        const currentId = currentParts.length > 0 ? currentParts[currentParts.length - 1] : null;
        const targetParts = location.pathname.split('/').filter(x => x.length > 0);
        const targetId = targetParts.length > 0 ? targetParts[targetParts.length - 1] : null;
        let locationCache = false;
        const onConfirm = force => {
            if (force) this.unblock();
            const { history } = this.props;
            history.push(locationCache.pathname);
        };

        const onCancel = () => {
            locationCache = false;
        };

        // We don't block the navigation if we're navigate to another form page for the
        // same item, or if we haven't made any changes to the object
        if (!!this._isModified && currentId !== targetId) {
            if (!!historyBlockHandler) {
                locationCache = location;
                historyBlockHandler(onConfirm, onCancel);
                return false;
            } else {
                return 'You have unsaved changes, are you sure you want to leave this page?';
            }
        }
    };

    onSave = e => {
        e.preventDefault();
        const { saveNotValid } = this.props;

        if (enableValidation) {
            const isValid = this.validateForm();

            if (!isValid) {
                !!this._isMounted && this.forceUpdate();
                if (!saveNotValid) return;
            }
        }

        const { onBeforeSave } = this.props;
        if (!onBeforeSave || onBeforeSave(this)) {
            this.executeSave();
        }
    };

    executeSave(params) {
        const { callback, refetchQueries } = params || {};
        const { customClient, client } = this.props;
        const mutation = this.getMutation();
        if (!!refetchQueries) {
            mutation.refetchQueries = refetchQueries;
            mutation.awaitRefetchQueries = true;
        }
        (customClient || client)
            .mutate(mutation)
            .then(this.onSaveCompleted)
            .catch(this.handleMutateError)
            .finally(callback);
        this._isSavePending = true;
        !!this._isMounted && this.forceUpdate();
    }

    /**
     * if you need to call save from the form object, this will verify whether it can actually execute
     */
    executeManualSave(params) {
        const { loading } = this.props;
        const hasData = Object.keys(this.getOriginalState()).length > 0;
        if (!hasData && loading) return;

        this.verifyIsModified();

        if (loading || this._isSavePending || !hasData || !this._isModified) return;

        this.executeSave(params);
    }

    onSaveCompleted = ({ data }) => {
        const updates = Object.keys(data)[0];
        this._isModified = false;
        this._isSavePending = false;

        const { onSaved, onLoad, onChange, saveMode } = this.props;

        if (!data[updates]) {
            console.log('warning! save function returned a null object!', data);
        }
        let updated = deleteTypeName(cloneDeep(data[updates]), true); // keep deletions
        try {
            if (onLoad) onLoad(updated);
            if (this.modifiedTabKeys && this.modifiedTabKeys.length) {
                this.modifiedTabKeys.forEach(tabKey => {
                    if (DataFormSaveMode.currentFragment === saveMode) {
                        Object.keys(this.tabLookup).forEach(key => {
                            if (this.tabLookup[key].key === tabKey) {
                                if (this.tabLookup[key].onLoad) {
                                    this.tabLookup[key].onLoad(updated);
                                }
                            }
                        });
                    } else {
                        const onLoadTab = this.tabLookup[tabKey].onLoad;
                        if (onLoadTab) onLoadTab(updated);
                    }
                });
            }
        } catch (err) {
            console.error('There was an error reloading the saved data, check onLoad().');
        }

        // update the form original
        const oldOriginal = this.getOriginalState();
        const newOriginal = { ...oldOriginal, ...updated };
        this.setOriginalState(newOriginal);

        // update the form state, retain unsaved data except for stale data
        const oldState = this.getTabState();
        const newState = mergeDataSources(oldState, updated);
        this.setTabState(deleteTypeName(newState)); // remove deletions

        try {
            if (onSaved) {
                onSaved({ ...newOriginal });
            }
        } catch (err) {
            console.error('There was an error reprocessing the saved data, check onSaved().');
        }

        try {
            if (onChange) {
                onChange(this.form);
            }
        } catch (err) {
            console.error('There was an error reprocessing the updated form, check onChange().');
        }

        // notify user (triggers a props reload)
        !!this._isMounted && this.props.setSnackbarMessage('Success, your changes have been saved.', true, 1000);
    };

    handleMutateError = e => {
        this._isSavePending = false;
        const { name, onGqlError } = this.props;
        if (onGqlError) onGqlError(e, `Mutation failed for '${name}'`);
        else console.error('gql error', e);
        !!this._isMounted && this.forceUpdate();
    };

    invalidate(allTabs) {
        const { tab, parentDataForm, context, name } = this.props;
        //console.log(`invalidating ${name}`);

        //console.log(`invalidated ${tab.id}`);
        if (!!allTabs) {
            for (let tabID in this.loaded) {
                if (this.loaded.hasOwnProperty(tabID)) {
                    this.loaded[tabID] = false;
                }
            }
        } else {
            const stateID = this.getStateID(tab.id);
            if (stateID === 'Combined' && !this.loaded[tab.id]) {
                this.loaded[tab.id] = false; //unload the tab so it can get loaded again before next render
            } else {
                this.loaded[stateID] = false; //unload the tab so it can get loaded again before next render
            }
        }

        if (parentDataForm && typeof val === 'object') {
            parentDataForm.invalidate(); //traverse to the parent and update
        } else {
            if (!context) console.error(`${name} needs context={this}, in order to trigger a re-render`);

            context.forceUpdate(); //force the context to rerender so the tab gets loaded
        }
    }

    renderSave() {
        const { loading, validate, buttonStyles } = this.props;

        const hasData = Object.keys(this.getOriginalState()).length > 0;
        if (!hasData && loading) return;

        this.verifyIsModified();

        const isValid = isNullOrUndefined(validate) || validate();

        const disabled = loading || this._isSavePending || !hasData || !this._isModified || !isValid;

        return [
            <Button
                variant="confirmation" className={cx(buttonStyles)}
                onClick={e => this.onSave(e)}
                disabled={disabled}
            >
                {this.renderButtonState(disabled)}
            </Button>,
            !disabled
        ];
    }


    renderStepperNext() {
        const { loading, validate, buttonStyles } = this.props;

        const hasData = Object.keys(this.getOriginalState()).length > 0;
        if (!hasData && loading) return;

        this.verifyIsModified();

        const isValid = isNullOrUndefined(validate) || validate();

        return (
            <Button
                variant="confirmation" className={cx(buttonStyles)}
                onClick={e => this.onStepperNext(e)}
                disabled={!isValid}
            >
                {this.renderButtonState(!isValid)}
            </Button>
        );
    }

    onStepperNext(e) {
        const { onStepperNext, loading } = this.props;

        const hasData = Object.keys(this.getOriginalState()).length > 0;
        const disabled = loading || this._isSavePending || !hasData || !this._isModified;

        if (!disabled) {
            this.onSave(e);
        }
        onStepperNext();
    }

    verifyIsModified(asList = false) {
        // Recalculate the modified state
        this.input = diff(this.getTabState(), this.getOriginalState(), false);

        const getDirty = Object.keys(this.input);
        if (this._isModified !== !!getDirty.length) {
            this._isModified = !!getDirty.length;
            setTimeout(() => !!this._isMounted && this.forceUpdate(), 1);
        }
        return !asList ? this._isModified : getDirty;
    }

    renderButtonState(disabled) {
        const { buttonLabels } = this.props;
        const { isSaving, isUnsaved, isNew, isSaved } = buttonLabels || {};
        const icon = disabled ? <TickIcon /> : <SaveIcon />;

        if (this._isSavePending) {
            return (
                <Fragment>
                    <Spinner size="xs" />
                    <Hidden xsDown>&nbsp;{isSaving || 'Saving...'}</Hidden>
                </Fragment>
            );
        } else if (Object.keys(this.getOriginalState()).length === 0) {
            return (
                <Fragment>
                    {icon}
                    <Hidden xsDown>&nbsp;{isNew || 'Save New'}</Hidden>
                </Fragment>
            );
        } else if (this._isModified) {
            return (
                <Fragment>
                    {icon}
                    <Hidden xsDown>&nbsp;{isUnsaved || 'Save Changes'}</Hidden>
                </Fragment>
            );
        }

        return (
            <Fragment>
                {icon}
                <Hidden xsDown>&nbsp;{isSaved || 'Saved'}</Hidden>
            </Fragment>
        );
    }
}


export default compose(withApollo, withRouter, withSnackbarMessage, withStyles({}))(DataForm);
